import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import React from 'react';
import { Camera, RotateCcw, RefreshCw, X } from 'lucide-react';
import styles from '../ReportCard.module.scss';

/**
 * Модальное окно QR-сканера с камерой / фото / отправкой.
 */
const ScannerOverlay = ({
  isGuard,
  scanningStep,
  capturedPhoto,
  videoRef,
  canvasRef,
  switchCamera,
  selectCamera,
  availableCameras,
  currentCameraInfo,
  isCameraLoading,
  capturePhoto,
  retakePhoto,
  handleSendPhoto,
  handleCloseScanner,
}) => {
  useInterfaceLanguage();
  return (
    <div className={styles.qrScannerOverlay}>
      <div className={styles.qrScannerModal} role="dialog" aria-modal="true" aria-label={tr("Сканирование и фото транспорта")}>
        {/* Верхняя панель */}
        <div className={styles.qrScannerHeader}>
          <div className={styles.headerLeft}>
            {tr(!isGuard && (
              <div className={styles.scanningSteps}>
                <div className={`${styles.stepBadge} ${scanningStep === 'scan' ? styles.active : (scanningStep !== 'scan' ? styles.completed : '')}`}>1</div>
                <div className={styles.stepDivider} />
                <div className={`${styles.stepBadge} ${scanningStep === 'photo' ? styles.active : (scanningStep === 'review' || scanningStep === 'sending' ? styles.completed : '')}`}>2</div>
              </div>
            ))}
            <h3>
              {tr(scanningStep === 'scan' && tr("СКАНИРОВАНИЕ QR"))}
              {tr(scanningStep === 'photo' && tr("ФОТО ТРАНСПОРТА"))}
              {tr(scanningStep === 'review' && tr("ПРОВЕРКА ФОТО"))}
              {tr(scanningStep === 'sending' && tr("ОТПРАВКА..."))}
            </h3>
          </div>
          <button onClick={handleCloseScanner} className={styles.closeButton} aria-label={tr("Закрыть сканер")}><X size={20} /></button>
        </div>

        {/* Область камеры */}
        <div className={styles.qrScannerBody}>
          {scanningStep === 'review' && capturedPhoto ? (
            <img src={capturedPhoto} alt={tr("Превью")} className={styles.photoPreview} />
          ) : (
            <div className={styles.videoContainer}>
              <video ref={videoRef} className={styles.qrVideo} playsInline muted autoPlay />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              {tr(scanningStep === 'scan' && (
                <div className={styles.qrOverlay}>
                  <div className={styles.qrFrame} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Нижняя панель управления */}
        <div className={styles.qrScannerFooter}>
          {tr(scanningStep === 'scan' && (
            <>
              {/* Список доступных камер (иконки) */}
              {tr(availableCameras && availableCameras.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '15px', flexWrap: 'wrap' }}>
                  {availableCameras.map((cam, idx) => {
                    const isActive = currentCameraInfo?.deviceId === cam.deviceId;
                    return (
                      <button
                        key={cam.deviceId}
                        onClick={() => selectCamera(cam.deviceId)}
                        disabled={isCameraLoading}
                        style={{
                          background: isActive ? "var(--theme-primary, #0d00f3)" : "var(--theme-surface-muted, #525c69)",
                          border: isActive ? "2px solid var(--theme-border, #fff)" : "1px solid var(--theme-border-strong, #555)",
                          borderRadius: '50%',
                          width: '50px',
                          height: '50px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: isCameraLoading ? 'not-allowed' : 'pointer',
                          opacity: isCameraLoading ? 0.6 : 1,
                          color: '#fff',
                          fontSize: '24px',
                          position: 'relative',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                          transition: 'all 0.2s',
                        }}
                        title={cam.label || tr("Камера {{value0}}", { value0: idx + 1 })}
                      >
                        <Camera size={23} />
                        <span style={{
                          position: 'absolute',
                          bottom: '-2px',
                          right: '-2px',
                          background: "var(--theme-surface-contrast, #000)",
                          borderRadius: '50%',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          width: '18px',
                          height: '18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: "1px solid var(--theme-border-strong, #777)"
                        }}>
                          {tr(idx + 1)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}
              
              {isCameraLoading ? (
                 <p className={styles.qrHint} style={{ color: "var(--theme-text-subtle, #aaa)" }}>{tr("⌛ Идет подключение к камере...")}</p>
              ) : (
                 <p className={styles.qrHint}>{tr("Наведите камеру на QR-код")}</p>
              )}

              <div className={styles.footerActions}>
                <button onClick={switchCamera} disabled={isCameraLoading} className={styles.btn_switch_camera} aria-label={tr("Переключить камеру")}><RefreshCw size={20} /></button>
                <button onClick={handleCloseScanner} className={styles.btn_close_scanner}>{tr("Закрыть")}</button>
              </div>
            </>
          ))}

          {tr(scanningStep === 'photo' && (
            <>
              <p className={styles.qrHint}>{tr("Направьте камеру на транспорт")}</p>
              <button onClick={capturePhoto} className={styles.capturePhotoBtn}><Camera size={20} />{tr("Сфотографировать")}</button>
            </>
          ))}

          {tr(scanningStep === 'review' && (
            <div className={styles.photoActions}>
              <button onClick={retakePhoto} className={styles.retakePhotoBtn}><RotateCcw size={18} />{tr("Переснять")}</button>
              <button
                onClick={handleSendPhoto}
                disabled={scanningStep === 'sending'}
                className={styles.sendPhotoBtn}
              >
                {scanningStep === 'sending' ? tr("⏳ Отправка...") : tr(" Отправить")}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ScannerOverlay);
